import { useQuery } from '@tanstack/react-query'
import {
  formatarDecimal,
  percentualPpm,
  ROTULO_ATIVIDADE_PRESUNCAO,
  ROTULO_REGIME_PLANEJAMENTO,
  type AtividadePresuncao,
  type CargaRegime,
  type RegimePlanejamento,
  type ResultadoPlanejamento,
} from '@via-tax/contracts'
import { Aviso, Cartao, EstadoVazio, Selo } from '@via-tax/ui'
import { useState } from 'react'
import { useExecutarSimulacao } from '../components/ExecutarSimulacao'
import { IcmsDasNotas } from '../components/IcmsDasNotas'
import { dataBr, reais } from '../formatos'
import type { ContextoSessao } from '../tipos'
import { useTRPC } from '../trpc'

/**
 * Planejamento de regime: Simples × Presumido × Real sobre as notas da empresa, com as premissas que só o
 * escritório conhece (folha, pró-labore, despesas ou lucro). Mesma simulação imutável das outras telas.
 */

const pct = percentualPpm
const REGIME_EMPRESA: Record<string, RegimePlanejamento> = { simples_nacional: 'SIMPLES', lucro_presumido: 'PRESUMIDO', lucro_real: 'REAL' }

export function PlanejamentoRegime({ contexto }: { contexto: ContextoSessao }) {
  const trpc = useTRPC()
  const podeExecutar = (contexto.capacidades as string[]).includes('executar_simulacao')
  const ultima = useQuery({
    ...trpc.simulacao.ultima.queryOptions(),
    refetchInterval: (q) => (q.state.data?.recente?.status === 'PROCESSANDO' ? 3000 : false),
  })
  const recente = ultima.data?.recente
  const concluida = ultima.data?.concluida
  const processando = recente?.status === 'PROCESSANDO'
  const simples = contexto.empresaAtiva?.regimeTributario === 'simples_nacional'
  const p = concluida?.resultado.planejamento
  const execucao = useExecutarSimulacao({
    premissasAtuais: concluida?.premissas ?? recente?.premissas,
    processando,
    jaRodou: !!concluida,
    simples,
    operacoes: concluida?.resultado.operacoes,
    secao: 'planejamento',
    abertoInicial: !p,
  })
  const regimeAtual = REGIME_EMPRESA[contexto.empresaAtiva?.regimeTributario ?? '']

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Simples × Presumido × Real</h1>
          <p className="mt-1 max-w-2xl text-sm text-tinta-2">
            Qual regime paga menos com a operação real da empresa: tributos sobre consumo das notas, IRPJ, CSLL e contribuição patronal, hoje, em 2027 e ao fim da transição.
            Simulação para decisão — não é apuração.
          </p>
        </div>
        {podeExecutar && execucao.botoes}
      </header>
      {podeExecutar && execucao.painel}

      <IcmsDasNotas />
      {p && !p.icmsNotas && (
        <Aviso tom="info">A última simulação é anterior à leitura do ICMS dos XML: {podeExecutar ? 'clique em "Simular de novo" para' : 'peça ao escritório para rodar de novo e'} usar esses valores na comparação.</Aviso>
      )}
      {processando && <Aviso tom="info">Calculando — a tela atualiza sozinha.</Aviso>}
      {recente?.status === 'ERRO' && <Aviso>A última simulação não terminou: {recente.erro}</Aviso>}
      {ultima.isPending ? (
        <p className="text-sm text-tinta-3">Carregando…</p>
      ) : !p ? (
        !processando && (
          <EstadoVazio titulo={concluida ? 'A última simulação é anterior a esta análise' : 'Nenhuma simulação ainda'}>
            {podeExecutar ? 'Preencha folha, pró-labore e despesas na aba "Planejamento de regime" das premissas e rode a simulação.' : 'O escritório ainda não rodou esta análise para a empresa.'}
          </EstadoVazio>
        )
      ) : (
        <Resultado p={p} regimeAtual={regimeAtual ?? p.regimeAtual} meta={{ criadoPor: concluida!.criadoPor, criadoEm: concluida!.criadoEm, versoes: concluida!.resultado.proveniencia }} />
      )}
    </div>
  )
}

function Resultado({ p, regimeAtual, meta }: { p: ResultadoPlanejamento; regimeAtual: RegimePlanejamento | null; meta: { criadoPor: string; criadoEm: string | Date; versoes: { versaoEngine: string; versaoParametros: string } } }) {
  const [ano, setAno] = useState(p.anos[1]?.ano ?? p.anos[0]!.ano)
  const a = p.anos.find((x) => x.ano === ano) ?? p.anos[0]!
  const melhor = a.regimes.find((r) => r.regime === a.melhor)!
  const atual = a.regimes.find((r) => r.regime === regimeAtual)
  const maior = Math.max(...a.regimes.map((r) => r.total), 1)
  const eq = p.equilibrioRealPresumido.find((x) => x.ano === ano)

  return (
    <div className="flex flex-col gap-5">
      <div role="tablist" className="inline-flex w-fit flex-wrap rounded-md border border-linha bg-painel p-0.5">
        {p.anos.map((x) => (
          <button key={x.ano} role="tab" aria-selected={x.ano === ano} onClick={() => setAno(x.ano)} className={`rounded px-3 py-1.5 text-sm ${x.ano === ano ? 'bg-navy text-white' : 'text-tinta-2 hover:text-tinta'}`}>
            {x.rotulo}
          </button>
        ))}
      </div>

      <section className={`rounded-lg border-l-4 bg-painel p-5 ${a.melhor === regimeAtual ? 'border-ok' : 'border-acao'}`}>
        <p className="text-xs text-tinta-3">Menor carga em {a.ano} · receita anual de {reais(p.receitaAnual)}</p>
        <p className="mt-1 text-2xl font-semibold">{ROTULO_REGIME_PLANEJAMENTO[a.melhor]}</p>
        <p className="mt-1 text-sm text-tinta-2">
          {a.melhor === regimeAtual
            ? `O regime atual já é o que paga menos: ${reais(melhor.total)} no ano (${pct(melhor.sobreReceita, 1)} da receita).`
            : atual
              ? atual.elegivel
                ? `Economia de ${reais(a.economiaContraAtual)} por ano contra o ${ROTULO_REGIME_PLANEJAMENTO[atual.regime]} (${reais(atual.total)} → ${reais(melhor.total)}).`
                : `O regime atual não é permitido para esta receita: ${atual.motivoInelegivel}`
              : `${reais(melhor.total)} no ano.`}
        </p>
      </section>

      <Cartao titulo="Carga total por regime">
        <div className="flex flex-col gap-2.5">
          {a.regimes.map((r) => (
            <div key={r.regime} className="grid grid-cols-1 gap-1 sm:grid-cols-[10rem_1fr_auto] sm:items-center sm:gap-3">
              <span className="flex items-center gap-1.5 text-sm font-medium">
                {ROTULO_REGIME_PLANEJAMENTO[r.regime]}
                {r.regime === regimeAtual && <Selo tom="neutro">atual</Selo>}
              </span>
              <div className="h-4 rounded bg-fundo">
                <div className={`h-4 rounded ${!r.elegivel ? 'bg-linha' : r.regime === a.melhor ? 'bg-ok' : 'bg-navy/70'}`} style={{ width: `${(r.total / maior) * 100}%` }} />
              </div>
              <span className="num text-sm sm:text-right">
                {r.elegivel ? reais(r.total) : 'não permitido'}
                <span className="block text-[11px] text-tinta-3">{r.elegivel ? `${pct(r.sobreReceita, 1)} da receita` : ''}</span>
              </span>
            </div>
          ))}
        </div>
      </Cartao>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {a.regimes.map((r) => (
          <PainelRegime key={r.regime} r={r} melhor={r.regime === a.melhor} atual={r.regime === regimeAtual} />
        ))}
      </div>

      {eq && (
        <Cartao titulo={`Lucro Real × Lucro Presumido em ${a.ano}`}>
          {eq.margem === null ? (
            <p className="text-sm">Com estes números o Lucro Presumido paga menos em qualquer margem de lucro.</p>
          ) : (
            <div className="flex flex-col gap-3 text-sm">
              <p>
                O <strong>Lucro Real</strong> passa a compensar quando o lucro antes do IR fica <strong>abaixo de {pct(eq.margem, 1)} da receita</strong> (
                {reais(Math.round((eq.margem * p.receitaAnual) / 1_000_000))} no ano).
                {eq.margemAtual !== null && ` Pelos dados informados, a margem é de ${pct(Math.max(0, eq.margemAtual), 1)}.`}
              </p>
              <div className="relative h-3 rounded bg-fundo" aria-hidden>
                <div className="absolute inset-y-0 left-0 rounded-l bg-ok/25" style={{ width: `${Math.min(100, eq.margem / 4_000)}%` }} />
                <div className="absolute -inset-y-1 w-0.5 bg-tinta-3" style={{ left: `${Math.min(100, eq.margem / 4_000)}%` }} />
                {eq.margemAtual !== null && <div className="absolute -inset-y-1.5 w-1.5 -translate-x-1/2 rounded bg-navy" style={{ left: `${Math.min(100, Math.max(0, eq.margemAtual) / 4_000)}%` }} />}
              </div>
              <div className="flex justify-between text-[11px] text-tinta-3">
                <span>0% de margem</span>
                <span>faixa verde: Lucro Real compensa · traço azul: empresa</span>
                <span>40%</span>
              </div>
            </div>
          )}
        </Cartao>
      )}

      {p.icmsNotas && (
        <Cartao titulo="ICMS lido dos XML (ano)">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Numero rotulo="ICMS destacado nas compras" valor={reais(p.icmsNotas.comprasDestacado)} nota="revenda e insumo, sem ST · vira crédito fora do Simples" />
            <Numero rotulo="Crédito de fornecedor do Simples" valor={reais(p.icmsNotas.comprasCreditoSimples)} nota="vCredICMSSN destacado nas notas" />
            <Numero
              rotulo="ICMS das vendas"
              valor={p.icmsNotas.vendasEstimado ? 'estimado' : reais(p.icmsNotas.vendasDestacado)}
              nota={p.icmsNotas.vendasEstimado ? `empresa do Simples: ${formatarDecimal(p.premissas.icmsVendas, 4)}% sobre as vendas com ICMS próprio` : 'destacado nas notas de venda'}
            />
            <Numero
              rotulo="Vendas sem ICMS próprio"
              valor={reais(p.icmsNotas.vendasSemIcmsProprio)}
              nota={`ST já retido, isentas ou não tributadas${p.icmsNotas.vendasComSt ? ` · ${reais(p.icmsNotas.vendasComSt)} com ST: no Simples, o ICMS sai do DAS` : ''}`}
            />
          </div>
        </Cartao>
      )}

      <Cartao titulo="O que mexe na escolha (2027)">
        <ul className="flex flex-col gap-1.5 text-sm">
          {p.sensibilidade.map((s) => (
            <li key={s.rotulo} className="flex flex-wrap items-center justify-between gap-2 border-t border-linha pt-1.5">
              <span>{s.rotulo}</span>
              <span className="flex items-center gap-2">
                <span className="text-tinta-2">{ROTULO_REGIME_PLANEJAMENTO[s.melhor]}</span>
                {s.vira ? <Selo tom="alerta">muda a escolha</Selo> : <Selo tom="ok">mantém</Selo>}
              </span>
            </li>
          ))}
        </ul>
      </Cartao>

      <Cartao titulo="Premissas e avisos">
        <ul className="list-disc space-y-1 pl-5 text-xs text-tinta-2">
          {p.presuncaoPorReceita && p.presuncaoPorReceita.length > 0 && (
            <li>
              Presunção do Lucro Presumido por receita:{' '}
              {p.presuncaoPorReceita.map((x) => `${ROTULO_ATIVIDADE_PRESUNCAO[x.atividade]} sobre ${reais(x.receita)}`).join(' · ')}
            </li>
          )}
          <li>
            Atividade principal: {ROTULO_ATIVIDADE_PRESUNCAO[p.atividade as AtividadePresuncao]} · folha {reais(p.premissas.folhaAnual)} · pró-labore {reais(p.premissas.proLaboreAnual)} · despesas{' '}
            {reais(p.premissas.despesasOperacionaisAnuais)} · compras das notas {reais(p.comprasAnual)}
          </li>
          {p.avisos.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-tinta-3">
          Motor {meta.versoes.versaoEngine} · parâmetros {meta.versoes.versaoParametros} (IRPJ, CSLL, presunção, CPP e limites gerados do texto das Leis 9.249, 7.689, 9.715, 9.718, 8.212,
          LC 123 e LC 224) · pedido por {meta.criadoPor} em {dataBr(meta.criadoEm)}
        </p>
      </Cartao>
    </div>
  )
}

function PainelRegime({ r, melhor, atual }: { r: CargaRegime; melhor: boolean; atual: boolean }) {
  const linhas: Array<{ rotulo: string; valor: number; recuo?: boolean; forte?: boolean }> = []
  const c = r.consumo
  linhas.push({ rotulo: 'Tributos sobre consumo', valor: c.total, forte: true })
  if (c.das) linhas.push({ rotulo: 'DAS', valor: c.das, recuo: true })
  if (c.pisCofins) linhas.push({ rotulo: 'PIS/COFINS', valor: c.pisCofins, recuo: true })
  if (c.icms) linhas.push({ rotulo: 'ICMS', valor: c.icms, recuo: true })
  if (c.iss) linhas.push({ rotulo: 'ISS', valor: c.iss, recuo: true })
  if (c.ipi) linhas.push({ rotulo: 'IPI', valor: c.ipi, recuo: true })
  if (c.cbs || c.ibs) linhas.push({ rotulo: 'CBS + IBS (débito)', valor: c.cbs + c.ibs, recuo: true })
  if (c.creditos) linhas.push({ rotulo: '(−) crédito de CBS/IBS', valor: -c.creditos, recuo: true })
  linhas.push({ rotulo: 'IRPJ e CSLL', valor: r.renda.total, forte: true })
  if (r.renda.total) {
    linhas.push({ rotulo: 'IRPJ 15%', valor: r.renda.irpj, recuo: true })
    if (r.renda.adicionalIrpj) linhas.push({ rotulo: 'Adicional do IRPJ', valor: r.renda.adicionalIrpj, recuo: true })
    linhas.push({ rotulo: 'CSLL 9%', valor: r.renda.csll, recuo: true })
  }
  linhas.push({ rotulo: 'INSS patronal', valor: r.folha.total, forte: true })
  if (r.folha.total) {
    linhas.push({ rotulo: 'CPP 20% + RAT', valor: r.folha.cpp + r.folha.rat, recuo: true })
    if (r.folha.terceiros) linhas.push({ rotulo: 'Terceiros', valor: r.folha.terceiros, recuo: true })
    if (r.folha.proLabore) linhas.push({ rotulo: 'Sobre pró-labore', valor: r.folha.proLabore, recuo: true })
  }
  return (
    <div className={`flex min-w-0 flex-col rounded-lg border bg-painel p-4 ${melhor ? 'border-ok ring-1 ring-ok/40' : 'border-linha'} ${!r.elegivel ? 'opacity-70' : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold">{ROTULO_REGIME_PLANEJAMENTO[r.regime]}</p>
        <span className="flex gap-1">
          {atual && <Selo tom="neutro">atual</Selo>}
          {melhor && <Selo tom="ok">menor carga</Selo>}
          {!r.elegivel && <Selo tom="alerta">não permitido</Selo>}
        </span>
      </div>
      <p className="num mt-1 text-xl font-semibold">{reais(r.total)}</p>
      <p className="text-[11px] text-tinta-3">
        {pct(r.sobreReceita, 1)} da receita{r.anexo ? ` · Anexo ${r.anexo}` : ''}
        {r.lucroAntesIr !== null ? ` · lucro antes do IR ${reais(r.lucroAntesIr)}` : ''}
      </p>
      {r.motivoInelegivel && <p className="mt-1 text-[12px] text-alerta">{r.motivoInelegivel}</p>}
      <dl className="mt-3 flex flex-col">
        {linhas.map((l) => (
          <div key={l.rotulo} className={`flex items-baseline justify-between gap-3 border-t border-linha py-1.5 text-sm ${l.forte ? 'font-semibold' : 'text-tinta-2'}`}>
            <dt className={`min-w-0 ${l.recuo ? 'pl-3 text-[13px]' : ''}`}>{l.rotulo}</dt>
            <dd className="num shrink-0 whitespace-nowrap">{reais(l.valor)}</dd>
          </div>
        ))}
      </dl>
      {r.observacoes.length > 0 && (
        <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[11px] text-tinta-3">
          {r.observacoes.map((o) => (
            <li key={o}>{o}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Numero({ rotulo, valor, nota }: { rotulo: string; valor: string; nota: string }) {
  return (
    <div className="rounded-md border border-linha p-3">
      <p className="text-[11px] text-tinta-3">{rotulo}</p>
      <p className="num mt-0.5 text-base font-semibold">{valor}</p>
      <p className="text-[11px] text-tinta-3">{nota}</p>
    </div>
  )
}
